import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { fetchPublicTrades } from 'api/apiExchange';
import {
  startPublicSubscription,
  stopSubscription,
} from 'api/services/webSocketApiService';
import { ApiTrade } from 'api/types';
import { useWebsocketEventListener } from 'components/hooks';
import { useSelector } from 'reduxState/store';

import { minutes, seconds } from './utils';

const TRADES_LIMIT = 100;

const tradesSortFn = (a: { sequence: number }, b: { sequence: number }) =>
  b.sequence - a.sequence;

/** Currently we check only WebSocket response P9-4986 */
const isSequenceValid = (trades: { sequence: number }[]) => {
  const sequenceIsValid = trades?.every((trade, index) =>
    trades[index + 1] ? trades[index + 1].sequence === trade.sequence - 1 : true
  );
  if (!sequenceIsValid) {
    console.warn(trades.slice(0, 5));
    console.error(`[tradesQueries] Invalid sequence`);
    return false;
  }
  return true;
};

// https://tanstack.com/query/v4/docs/react/guides/updates-from-mutation-responses
// ISSUE: https://github.com/TanStack/query/issues/1633
export const useMarketTradesQuery = (marketPair?: string) => {
  const websocketConnected = useSelector(
    state => state.exchange.websocketState.connected
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!marketPair || !websocketConnected) {
      return;
    }
    startPublicSubscription({
      name: 'trades',
      markets: [marketPair],
    });
    return () => {
      stopSubscription({
        name: 'trades',
        markets: [marketPair],
      });
    };
  }, [websocketConnected, marketPair]);

  useWebsocketEventListener('trades', event => {
    if (event.data.market !== marketPair) {
      console.warn(
        'Ignoring trade from different market',
        marketPair,
        event.data
      );
      return;
    }
    queryClient.setQueryData<ApiTrade[]>(
      ['trades', 'last100', event.data.market],
      existingTrades => {
        if (!existingTrades) {
          return undefined; // README P9-4765
        }

        if (existingTrades.find(trade => trade.fillId === event.data.fillId)) {
          // Fill already exists. Expected only in case:
          //  User went offline and returns in couple seconds, API is refetched (was stale),
          //  and also WebSocket can send "offline" trades so we get duplicated.
          console.warn(
            `[tradesQueries] WS sent duplicated fill. Ignoring ID ${event.data.fillId}`
          );
          return undefined;
        }

        const updatedTrades = [event.data, ...existingTrades]
          .sort(tradesSortFn)
          .slice(0, TRADES_LIMIT);

        // Only check most recent 2 events (compare added from WS only)
        if (isSequenceValid(updatedTrades.slice(0, 2))) {
          return updatedTrades;
        }

        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ['trades', 'last100', event.data.market],
          });
        });
        return undefined; // Do not modify cache
      }
    );
  });

  return useQuery({
    queryKey: ['trades', 'last100', marketPair],
    queryFn: !marketPair
      ? skipToken
      : () =>
          fetchPublicTrades(marketPair, TRADES_LIMIT).then(response => {
            // Backend returns from old to most recent, but we need from recent to older
            const responseTrades = response.reverse().sort(tradesSortFn);
            return responseTrades;
            // if (isSequenceValid(responseTrades)) {
            //   return responseTrades;
            // }
            // console.error(['[tradesQueries] API returned incorrect sequence']);
            // return [];
          }),
    staleTime: seconds(5),
    refetchOnWindowFocus: false,
    refetchInterval: minutes(10),
    // refetchInterval: query => {
    //   // There might be websocket events until we fetch data,
    //   // in such case we will retrigger fetch of data.....

    //   const defaultInterval = minutes(10);
    //   const invalidDataInterval = seconds(1);

    //   const data = query.state.data;

    //   if (!data?.length) {
    //     return defaultInterval;
    //   }
    //   // Initialize refetch quickly if we missed some trade
    //   return isSequenceValid(data) ? defaultInterval : invalidDataInterval;
    // },
  });
};

// TRY 1 (react-query) caching:

// const TRADES_LIMIT = 100;

// const tradesSortFn = (a: { sequence: number }, b: { sequence: number }) =>
//   b.sequence - a.sequence;

// // https://tanstack.com/query/v4/docs/react/guides/updates-from-mutation-responses
// export const useMarketTradesQuery = (marketPair?: string) => {
//   const websocketConnected = useSelector(
//     state => state.exchange.websocketState.connected
//   );
//   const queryClient = useQueryClient();

//   useEffect(() => {
//     if (!marketPair || !websocketConnected) {
//       return;
//     }
//     startSubscription({ name: 'trades', markets: [marketPair] });
//     return () => {
//       stopSubscription({ name: 'trades', markets: [marketPair] });
//     };
//   }, [websocketConnected, marketPair]);

//   useWebsocketEventListener('trades', event => {
//     if (event.data.market !== marketPair) {
//       return;
//     }
//     queryClient.setQueryData<ApiTrade[]>(
//       ['trades', 'last100', marketPair],
//       existingTrades =>
//         [event.data, ...(existingTrades || [])].slice(0, TRADES_LIMIT)
//     );
//   });

//   useEffect(() => {
//     if (!marketPair) {
//       return;
//     }
//     fetchPublicTrades(marketPair, TRADES_LIMIT).then(response => {
//       // Backend returns from old to most recent, but we need from recent to older
//       const responseSorted = response.reverse();

//       queryClient.setQueryData<ApiTrade[]>(
//         ['trades', 'last100', marketPair],
//         /** Previous, possibly missed trades (websocket collected) */
//         previousTrades => {
//           const previousTradesIds = new Set(
//             (previousTrades || []).map(trade => trade.fillId)
//           );
//           return (
//             [...previousTrades, ...responseSorted]
//               // Remove possible duplicates
//               .filter(trade => !previousTradesIds.has(trade.fillId))
//               .sort(tradesSortFn)
//               .slice(0, TRADES_LIMIT)
//           );
//         }
//       );
//     });
//   }, [marketPair, queryClient]);

//   return useQuery<ApiTrade[]>(
//     ['trades', 'last100', marketPair],
//     () => {
//       // For each market pair we clear data
//       // and then rely on websocket and fetch to update it
//       // and keep it updated
//       return [];
//     },
//     {
//       enabled: !!marketPair,
//       staleTime: 1000 * 60, // 1 minute
//       refetchInterval: 1000 * 60 * 10, // 10 minutes
//     }
//   );
// };

// ORIGINAL (before react-query):

// const visibleDecimals =
//     useSelectMarketTickSize(currentMarketPair)?.visibleDecimals ??
//     EXCHANGE_DECIMALS;
//   // const websocketMissedTradesRef = useRef<ApiTrade[]>([]);
//   const [trades, setTrades] = useState<ApiTrade[] | null>(null);

//   useEffect(() => {
//     if (!currentMarketPair || !websocketConnected) {
//       return;
//     }
//     startSubscription({ name: 'trades', markets: [currentMarketPair] });
//     fetchPublicTrades(currentMarketPair, TRADES_LIMIT).then(response => {
//       // Backend returns from old to most recent, but we need from recent to older
//       const responseSorted = response.reverse().sort(tradesSortFn);
//       if (websocketMissedTradesRef.current.length > 0) {
//         const tradesIds = new Set(responseSorted.map(trade => trade.fillId));
//         const tradesMissed = websocketMissedTradesRef.current.filter(
//           trade => !tradesIds.has(trade.fillId)
//         );
//         websocketMissedTradesRef.current = [];
//         setTrades([...tradesMissed, ...responseSorted].sort(tradesSortFn));
//         return;
//       }
//       setTrades(responseSorted);
//     });
//     return () => {
//       stopSubscription({ name: 'trades', markets: [currentMarketPair] });
//       setTrades([]);
//     };
//   }, [websocketConnected, currentMarketPair]);

//   useWebsocketEventListener('trades', event => {
//     if (event.data.market !== currentMarketPair) {
//       return;
//     }
//     if (trades === null) {
//       // Collect all trades between fetch is running
//       websocketMissedTradesRef.current.push(event.data);
//       return;
//     }
//     setTrades(existingTrades =>
//       [event.data, ...(existingTrades || [])].slice(0, TRADES_LIMIT)
//     );
//   });
